<template>
  <v-dialog v-model="open" max-width="600px" :persistent="loading">
    <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
      <v-card>
        <dialog-title
          v-model="open"
          :title="`${item.isNew ? 'Add' : 'Edit'} Entity`"
          :disabled="loading"
          :loading="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              Fill out the form below to edit <strong>details</strong>.
            </v-col>
            <v-col cols="12" md="3">
              <ValidationProvider
                rules="required|noPeriods"
                v-slot="{ errors }"
                vid="Id"
              >
                <v-text-field
                  v-model="item.id"
                  label="Id*"
                  :filled="!editableId"
                  :readonly="!editableId"
                  :outlined="editableId"
                  :disabled="loading"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="9">
              <ValidationProvider
                rules="required|min:2|max:255"
                v-slot="{ errors }"
                vid="Name"
              >
                <v-text-field
                  v-model="item.name"
                  label="Name*"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                rules="min:2|max:500"
                v-slot="{ errors }"
                vid="Address1"
              >
                <v-text-field
                  v-model="item.address1"
                  label="Address 1"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <ValidationProvider
                rules="min:2|max:500"
                v-slot="{ errors }"
                vid="Address2"
              >
                <v-text-field
                  v-model="item.address2"
                  label="Address 2"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6" lg="7" xl="7">
              <ValidationProvider
                rules="min:2|max:200"
                v-slot="{ errors }"
                vid="City"
              >
                <v-text-field
                  v-model="item.city"
                  label="City"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2" xl="2">
              <ValidationProvider
                rules="min:2|max:2"
                v-slot="{ errors }"
                vid="State"
              >
                <v-text-field
                  v-model="item.state"
                  label="State"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="3" xl="3">
              <ValidationProvider
                rules="min:5|max:10"
                v-slot="{ errors }"
                vid="ZipCode"
              >
                <v-text-field
                  v-model="item.zipCode"
                  label="Zip Code"
                  outlined
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" sm="4">
              <ValidationProvider
                :rules="{
                  regex: /^(\+0?1\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                }"
                v-slot="{ errors }"
                vid="Phone"
              >
                <v-text-field
                  v-model="item.phone"
                  label="Phone"
                  outlined
                  v-mask="'###-###-####'"
                  :error-messages="errors"
                  :disabled="loading"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" class="d-flex align-center">
              <small class="flex-fill">*indicates required field</small>
              <v-btn :disabled="loading" text color="primary" @click="close">
                Cancel
              </v-btn>
              <v-btn :loading="loading" type="submit" color="primary">
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </ValidationObserver>
  </v-dialog>
</template>

<script>
import DialogTitle from "../../components/DialogTitle.vue";
import entityService from "../../services/entityService";

import { settings, roles } from "../../constants/Identity";

const newItem = () => ({
  id: null,
  name: null,
  address1: null,
  address2: null,
  city: null,
  state: null,
  zipCode: null,
  children: null,
  contacts: [],
  metaData: [],
  isNew: true,
});

export default {
  name: "EntityEditor",
  components: { DialogTitle },
  data: () => ({
    open: false,
    item: newItem(),
    pendingChanges: 0,
    path: null,
  }),
  computed: {
    loading() {
      return this.pendingChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
    editableId() {
      return this.item.isNew || this.$store.state.user.role === roles.admin;
    },
  },
  watch: {
    open(val, prev) {
      if (val != prev) {
        if (!val) this.close();
      }
    },
  },
  methods: {
    edit(entity, path) {
      this.path = path;
      this.item = entity ? JSON.parse(JSON.stringify(entity)) : newItem();
      this.$refs.observer?.reset();
      this.open = true;
    },
    close() {
      this.open = false;
    },
    async onSubmit() {
      let isValid = await this.$refs.observer.validate();
      if (!isValid) return;

      this.pendingChanges++;

      var pathParts =
        this.path.length > 0
          ? this.path.split(settings.entityPathSeparator)
          : [];
      if (this.item.isNew) pathParts.push(this.item.id);

      entityService
        .upsert(
          this.tenantId,
          pathParts.join(settings.entityPathSeparator),
          this.item
        )
        .then(
          () => {
            this.close();
            this.$emit("reload");
            this.$store.dispatch(
              "notifications/success",
              `Successfully updated entity.`
            );
          },
          (err) => {
            if (err.statusCode === 400) {
              this.$refs.observer.setErrors(err.result);
            } else {
              this.$store.dispatch(
                "notifications/error",
                `An unexpected error occurred while trying to ${
                  this.isNew ? "create" : "update"
                } user. Please try again later.`
              );
            }
          }
        )
        .finally(() => this.pendingChanges--);
    },
  },
};
</script>
